/* eslint-disable import/no-cycle */
import API from 'base/auth/constants';
import { CoreSliceActions } from 'base/core/slice';
import ENVIRONMENT from 'config';
import {
  clearAuthToken,
  getAuthToken, getRefreshToken, setAuthToken, setRefreshToken,
} from 'helpers/token';
import { IRestApiResponse } from 'interfaces';
import { isArray } from 'lodash';
import jwt_decode from 'jwt-decode';
import { getUserHubUrl } from './common-utils';

let store: any;
export const injectStore = (_store: any) => {
  store = _store;
};
let enterpriseId: string;

export class ResponseError extends Error {
  public response: Response;

  constructor(response: Response) {
    super(response.statusText);
    this.response = response;
  }
}

/**
 * Parses the JSON returned by a network request
 *
 * @param  {object} response A response from a network request
 *
 * @return {object} The parsed JSON from the request
 */
const parseJSON = async (response: Response) => {
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  if (!response.ok) {
    try {
      return await response.clone().text().then((text) => JSON.parse(text));
    } catch (error) {
      return response;
    }
  }
  return response.json();
};

/**
 * use the refresh token to get the new tokens
 */
// eslint-disable-next-line consistent-return
const refreshToken = async () => {
  const currentRefreshToken = getRefreshToken();

  // If no refresh token exists, redirect to login immediately
  if (!currentRefreshToken) {
    console.log('No refresh token found, redirecting to login');
    clearAuthToken();
    if (!window.location.pathname.includes('login')) {
      window.location.href = '/login';
    }
    return false;
  }

  try {
    const fetchResponse = await fetch(`${ENVIRONMENT.BACKEND_API}${API.POST_REFRESH_TOKEN.path}`, {
      body: JSON.stringify({
        token: currentRefreshToken,
        email: (jwt_decode(getAuthToken() as string) as any).username,
      }),
      method: API.POST_REFRESH_TOKEN.method,
      mode: 'cors',
      cache: 'no-cache',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (![200, 201].includes(fetchResponse.status)) {
      try {
        // Check specifically for the unauthorized format
        const errorData = await fetchResponse.json();
        if (errorData && errorData.statusCode === 401 && errorData.message === 'Unauthorized' && errorData.traceId) {
          console.log('Refresh token expired or invalid, logging out user', errorData.traceId);
        }
      } catch (parseError) {
        console.error('Error parsing refresh token failure response', parseError);
      }

      clearAuthToken();
      if (!window.location.pathname.includes('login')) {
        window.location.href = '/login';
      }
      return false;
    }

    const res = (await fetchResponse.json()) as IRestApiResponse;
    setAuthToken(res.data.idToken.jwtToken);
    setRefreshToken(res.data.refreshToken.token);

    if (window.location.pathname.includes('login')) {
      window.location.href = '/';
    }
    return true;
  } catch (error) {
    console.error('Error during token refresh:', error);
    clearAuthToken();
    if (!window.location.pathname.includes('login')) {
      window.location.href = '/login';
    }
    return false;
  }
};

/**
 * Checks if a network request came back fine, and throws an error if not
 *
 * @param  {object} response   A response from a network request
 *
 * @return {object|undefined} Returns either the response, or throws an error
 */
const checkStatus = async (response: Response) => {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  if ([401, 423].includes(response.status)) {
    const data: any = await parseJSON(response);
    if (data.errors && data.errors.userLoggedOut) {
      window.location.reload();
      return false;
    }
    return response;
  }

  const res = (await response.json()) as IRestApiResponse;
  const apiMessage = res.message ?? 'Something went wrong';
  store.dispatch(
    CoreSliceActions.setMessage({
      message: isArray(apiMessage) ? res.message.join(', ') : res.message,
      success: false,
    }),
  );
  const error = new ResponseError(response);
  error.response = response;
  throw error;
};

/**
 * Requests a URL, returning a promise
 *
 * @param  {string} url       The URL we want to request
 * @param  {object} [options] The options we want to pass to "fetch"
 *
 * @return {object}           The response data
 */
export async function request(
  _metadata: any,
  data: any,
  isSecure: boolean = true,
  isFormData: boolean = false,
  url: string = ENVIRONMENT.BACKEND_API,
  isRetry: boolean = false, // Flag to prevent infinite retry loops
): Promise<any> {
  const metadata = { ..._metadata };
  const pathTokens = metadata.path.split(':');
  if (metadata.path.indexOf(':') !== 0) {
    pathTokens.shift();
  }
  pathTokens.forEach((token: string) => {
    metadata.path = metadata.path.replace(`:${token}`, `${data[token]}`);
  });

  const backEndUrl = `${url}${metadata.path}`;
  const formData = new FormData();
  if (isFormData) {
    Object.keys(data).forEach((key) => formData.append(key, data[key]));
  }

  // check if the token is expires
  const jwtToken = getAuthToken();
  if (jwtToken) { // Check if the token is expired
    if (!isRetry) { // Only try to refresh if not already in a retry
      try {
        const decodedToken = jwt_decode(jwtToken as string) as any;
        if (decodedToken.exp < (Date.now() / 1000)) {
          console.log('Token expired, attempting refresh');
          const refreshResult = await refreshToken();
          if (refreshResult) {
            console.log('Token refreshed successfully, retrying request');
            return await request(_metadata, data, isSecure, isFormData, url, true);
          }
          console.log('Token refresh failed, not retrying request');
          return { statusCode: 401, message: 'Authentication failed' };
        }
      } catch (error) {
        console.error('Error decoding token:', error);
      }
    }
  }

  const coreState = store.getState()['feature/base/core'];
  if (store && coreState) {
    enterpriseId = coreState.enterprise.id;
  }

  const options: RequestInit = {
    method: metadata.method,
    mode: 'cors', // no-cors, cors, same-origin
    cache: 'no-cache',
    credentials: 'include', // include, *same-origin, omit
    ...(metadata.signal && { signal: metadata.signal }),
    headers: {
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Methods': 'GET,PUT,POST,DELETE,PATCH,OPTIONS',
      ...(isSecure && {
        Authorization: `Bearer ${getAuthToken() || ''}`,
      }),
      ...(!isFormData && {
        'Content-Type': 'application/json',
      }),
      'X-ENT-ID': `${enterpriseId}`,
      'x-platform-id': 'expert-hub',
    },
    redirect: 'follow', // manual, *follow, error
    referrer: 'no-referrer', // no-referrer, *client
    ...(['POST', 'PUT', 'PATCH', 'DELETE'].includes(metadata.method) && {
      body: isFormData ? formData : JSON.stringify(data),
    }),
  };

  let fetchResponse: Response;
  try {
    fetchResponse = await fetch(backEndUrl, options);
  } catch (error) {
    if (error instanceof TypeError) {
      const userHubUrl = getUserHubUrl();
      const [subdomain, ...domainParts] = userHubUrl.split('.');
      const challengeUrl = `https://challenge.${domainParts.join('.')}`;

      const redirectUrl = new URL(challengeUrl);
      redirectUrl.searchParams.set('callbackUrl', window.location.pathname);
      redirectUrl.searchParams.set('platformId', 'expert');
      redirectUrl.searchParams.set('subdomain', subdomain);

      window.location.href = redirectUrl.toString();
    }
    console.error(error);
    throw error;
  }

  const response = await checkStatus(fetchResponse);
  if (!response) {
    return false;
  }

  if (isSecure && response.status === 401 && !isRetry) {
    // Try to refresh the token only if this is not already a retry request
    const refreshResult = await refreshToken();
    if (refreshResult) {
      console.log('Token refreshed after 401 response, retrying request');
      return request(_metadata, data, isSecure, isFormData, url, true);
    }
    console.log('Token refresh failed after 401 response, not retrying');
    return { statusCode: 401, message: 'Authentication failed' };
  }

  if (response.status === 423) {
    console.log('User has been deleted. Logging out from the system');
    clearAuthToken();
    if (!window.location.pathname.includes('login')) {
      window.location.href = '/login';
    }
  }
  return parseJSON(response);
}
